// ============================================================================
// ADMIN DATING - LISTE DES COMMANDES PRO
// Gestion complète des commandes et paiements Dating
// Version professionnelle avec statistiques avancées et design moderne
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleSelect,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  DollarSign,
  ArrowLeft,
  Search,
  Loader2,
  RefreshCw,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Smartphone,
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Crown,
  Mail,
  Phone,
  Copy,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Wallet,
  Receipt,
  Sparkles,
  User,
  X,
  ShieldCheck
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../components/ui/use-toast';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { DatingOrder, DatingPlan, DatingProfile } from '../../../types/dating';
import { format, formatDistanceToNow, subDays, startOfDay, endOfDay, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

// ============================================================================
// TYPES
// ============================================================================

interface OrderWithDetails extends DatingOrder {
  plan?: DatingPlan;
  profile?: DatingProfile;
}

type FilterStatus = 'all' | 'pending' | 'paid' | 'failed' | 'refunded';
type FilterPaymentMethod = 'all' | 'mtn' | 'orange' | 'lygos' | 'stripe' | 'paypal';
type FilterPeriod = 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'last_month';
type SortField = 'created_at' | 'amount' | 'customer_name' | 'status';
type SortDirection = 'asc' | 'desc';

interface OrderStats {
  total: number;
  pending: number;
  paid: number;
  failed: number;
  refunded: number;
  revenue: number;
  revenueToday: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueGrowth: number;
  avgOrderValue: number;
  conversionRate: number;
  topPlan: string;
  topPaymentMethod: string;
}

// ============================================================================
// UTILITAIRES
// ============================================================================

// Affiche toujours le montant complet en FCFA (ex: 32000 FCFA)
const formatAmount = (amount: number): string => {
  return `${Math.round(amount)} FCFA`;
};

// Ancien format compact (non utilisé mais conservé si besoin futur)
const formatCompactAmount = (amount: number): string => {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
  return amount.toString();
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30' },
  paid: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30' },
  failed: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
  refunded: { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-500/30' },
};

// Mini graphique en barres
const MiniBarChart = ({ data, color = 'green' }: { data: number[]; color?: string }) => {
  const max = Math.max(...data, 1);
  const colorClasses: Record<string, string> = { green: 'bg-green-500', pink: 'bg-pink-500', yellow: 'bg-yellow-500', blue: 'bg-blue-500' };
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((value, index) => (
        <div key={index} className={`w-2 ${colorClasses[color]} rounded-t opacity-70 hover:opacity-100 transition-all`}
          style={{ height: `${Math.max((value / max) * 100, 5)}%` }} title={`${value}`} />
      ))}
    </div>
  );
};

// Barre de progression circulaire
const CircularProgress = ({ value, max, color = 'blue' }: { value: number; max: number; color?: string }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const radius = 24; const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  const colorClasses: Record<string, string> = { blue: 'stroke-blue-500', green: 'stroke-green-500', yellow: 'stroke-yellow-500', pink: 'stroke-pink-500' };
  return (
    <div className="relative" style={{ width: 60, height: 60 }}>
      <svg className="transform -rotate-90" width={60} height={60}>
        <circle cx={30} cy={30} r={radius} strokeWidth={6} fill="none" className="stroke-white/10" />
        <circle cx={30} cy={30} r={radius} strokeWidth={6} fill="none" strokeLinecap="round"
          className={`${colorClasses[color]} transition-all duration-500`}
          style={{ strokeDasharray: circumference, strokeDashoffset: offset }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-white">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
};

export default function DatingOrderList() {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  // États principaux
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<FilterPaymentMethod>('all');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  
  // Tri et pagination
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Modal
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Stats
  const [stats, setStats] = useState<OrderStats>({
    total: 0, pending: 0, paid: 0, failed: 0, refunded: 0,
    revenue: 0, revenueToday: 0, revenueThisMonth: 0, revenueLastMonth: 0,
    revenueGrowth: 0, avgOrderValue: 0, conversionRate: 0,
    topPlan: 'N/A', topPaymentMethod: 'N/A'
  });
  const [weeklyRevenue, setWeeklyRevenue] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('dating_orders')
        .select(`
          *,
          plan:dating_plans (*),
          profile:dating_profiles (id, display_name, email, avatar_url, is_vip, is_verified)
        `)
        .order('created_at', { ascending: false });

      query = applyDomainFilterToQuery(query);
      const { data, error } = await query;

      if (error) throw error;
      const allOrders = (data || []) as OrderWithDetails[];
      setOrders(allOrders);
      calculateStats(allOrders);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les commandes', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateStats = (allOrders: OrderWithDetails[]) => {
    const today = new Date();
    const startOfToday = startOfDay(today);
    const thisMonthStart = startOfMonth(today);
    const lastMonthStart = startOfMonth(subMonths(today, 1));
    const lastMonthEnd = endOfMonth(subMonths(today, 1));

    const pending = allOrders.filter(o => o.status === 'pending').length;
    const paid = allOrders.filter(o => o.status === 'paid').length;
    const failed = allOrders.filter(o => o.status === 'failed').length;
    const refunded = allOrders.filter(o => o.status === 'refunded').length;

    const paidOrders = allOrders.filter(o => o.status === 'paid');
    const revenue = paidOrders.reduce((sum, o) => sum + (o.amount || 0), 0);
    const revenueToday = paidOrders.filter(o => o.paid_at && new Date(o.paid_at) >= startOfToday).reduce((sum, o) => sum + (o.amount || 0), 0);
    const revenueThisMonth = paidOrders.filter(o => o.paid_at && new Date(o.paid_at) >= thisMonthStart).reduce((sum, o) => sum + (o.amount || 0), 0);
    const revenueLastMonth = paidOrders.filter(o => {
      if (!o.paid_at) return false;
      const d = new Date(o.paid_at);
      return d >= lastMonthStart && d <= lastMonthEnd;
    }).reduce((sum, o) => sum + (o.amount || 0), 0);

    const revenueGrowth = revenueLastMonth > 0 ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth * 100) : 0;
    const avgOrderValue = paidOrders.length > 0 ? revenue / paidOrders.length : 0;
    const conversionRate = allOrders.length > 0 ? (paid / allOrders.length * 100) : 0;

    const planCounts: Record<string, number> = {};
    paidOrders.forEach(o => { const n = o.plan?.name || 'Inconnu'; planCounts[n] = (planCounts[n] || 0) + 1; });
    const topPlan = Object.entries(planCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    const methodCounts: Record<string, number> = {};
    paidOrders.forEach(o => { const m = o.payment_method || 'Inconnu'; methodCounts[m] = (methodCounts[m] || 0) + 1; });
    const topPaymentMethod = Object.entries(methodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    const weeklyData = [];
    for (let i = 6; i >= 0; i--) {
      const day = subDays(today, i);
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayRevenue = paidOrders.filter(o => {
        if (!o.paid_at) return false;
        const d = new Date(o.paid_at);
        return d >= dayStart && d <= dayEnd;
      }).reduce((sum, o) => sum + (o.amount || 0), 0);
      weeklyData.push(dayRevenue);
    }
    setWeeklyRevenue(weeklyData);

    setStats({
      total: allOrders.length, pending, paid, failed, refunded, revenue, revenueToday,
      revenueThisMonth, revenueLastMonth, revenueGrowth: Math.round(revenueGrowth * 10) / 10,
      avgOrderValue: Math.round(avgOrderValue), conversionRate: Math.round(conversionRate * 10) / 10,
      topPlan, topPaymentMethod
    });
  };

  useEffect(() => { fetchOrders(); }, [tenant]);

  // ============================================================================
  // FILTRAGE ET TRI
  // ============================================================================

  const filteredAndSortedOrders = useMemo(() => {
    let result = [...orders];

    // Filtre par recherche
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.order_number?.toLowerCase().includes(q) ||
        order.customer_name?.toLowerCase().includes(q) ||
        order.customer_email?.toLowerCase().includes(q) ||
        order.profile?.display_name?.toLowerCase().includes(q) ||
        order.plan?.name?.toLowerCase().includes(q)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') result = result.filter(order => order.status === filterStatus);

    // Filtre par méthode de paiement
    if (filterPaymentMethod !== 'all') result = result.filter(order => order.payment_method === filterPaymentMethod);

    // Filtre par période
    if (filterPeriod !== 'all') {
      const today = new Date();
      let startDate: Date; let endDate: Date = endOfDay(today);
      switch (filterPeriod) {
        case 'today': startDate = startOfDay(today); break;
        case 'yesterday': startDate = startOfDay(subDays(today, 1)); endDate = endOfDay(subDays(today, 1)); break;
        case 'week': startDate = subDays(today, 7); break;
        case 'month': startDate = startOfMonth(today); break;
        case 'last_month': startDate = startOfMonth(subMonths(today, 1)); endDate = endOfMonth(subMonths(today, 1)); break;
        default: startDate = new Date(0);
      }
      result = result.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    // Tri
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'created_at': comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
        case 'amount': comparison = (a.amount || 0) - (b.amount || 0); break;
        case 'customer_name': comparison = (a.customer_name || '').localeCompare(b.customer_name || ''); break;
        case 'status': comparison = (a.status || '').localeCompare(b.status || ''); break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [orders, searchQuery, filterStatus, filterPaymentMethod, filterPeriod, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage);
  const paginatedOrders = filteredAndSortedOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterStatus, filterPaymentMethod, filterPeriod]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleRefresh = () => { setRefreshing(true); fetchOrders(); };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('desc'); }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedOrder) return;
    setProcessing(true);
    try {
      const { error } = await supabase.from('dating_orders').update({ status: 'paid', paid_at: new Date().toISOString() }).eq('id', selectedOrder.id);
      if (error) throw error;

      // Activer l'abonnement
      if (selectedOrder.plan_id && selectedOrder.profile_id) {
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + (selectedOrder.plan?.duration_days || 30));
        const { domain, tenantId } = getDomainFilterParams();
        await supabase.from('dating_subscriptions').insert({
          profile_id: selectedOrder.profile_id, plan_id: selectedOrder.plan_id,
          tenant_id: parseInt(tenantId), domain, status: 'active', is_active: true,
          started_at: new Date().toISOString(), expires_at: expiresAt.toISOString(),
          activated_at: new Date().toISOString(), payment_reference: selectedOrder.order_number,
          payment_method: selectedOrder.payment_method, amount_paid: selectedOrder.amount, currency: selectedOrder.currency
        });
        await supabase.from('dating_profiles').update({ is_vip: true, vip_until: expiresAt.toISOString() }).eq('id', selectedOrder.profile_id);
      }

      toast({ title: '✅ Commande validée', description: `La commande ${selectedOrder.order_number} a été marquée comme payée.` });
      setShowDetailModal(false);
      fetchOrders();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la commande', variant: 'destructive' });
    } finally { setProcessing(false); }
  };

  const handleRefund = async () => {
    if (!selectedOrder) return;
    setProcessing(true);
    try {
      const { error } = await supabase.from('dating_orders').update({ status: 'refunded', updated_at: new Date().toISOString() }).eq('id', selectedOrder.id);
      if (error) throw error;
      toast({ title: '💰 Remboursement enregistré', description: `La commande ${selectedOrder.order_number} a été marquée comme remboursée.` });
      setShowDetailModal(false);
      fetchOrders();
    } catch (error) {
      console.error('Erreur remboursement:', error);
      toast({ title: 'Erreur', description: 'Impossible de traiter le remboursement', variant: 'destructive' });
    } finally { setProcessing(false); }
  };

  const handleCopyOrderNumber = (orderNumber: string) => {
    navigator.clipboard.writeText(orderNumber);
    toast({ title: 'Copié !', description: `N° ${orderNumber} copié dans le presse-papier` });
  };

  const handleExportCSV = () => {
    const headers = ['N° Commande', 'Client', 'Email', 'Plan', 'Montant', 'Devise', 'Méthode', 'Statut', 'Date création', 'Date paiement'];
    const rows = filteredAndSortedOrders.map(order => [
      order.order_number, order.customer_name, order.customer_email, order.plan?.name || 'N/A',
      order.amount, order.currency, order.payment_method || 'N/A', order.status,
      format(new Date(order.created_at), 'dd/MM/yyyy HH:mm'),
      order.paid_at ? format(new Date(order.paid_at), 'dd/MM/yyyy HH:mm') : ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commandes-dating-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: '📥 Export réussi', description: `${filteredAndSortedOrders.length} commandes exportées` });
  };

  // ============================================================================
  // BADGES ET ICÔNES
  // ============================================================================

  const getStatusBadge = (status: string) => {
    const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
    const icons: Record<string, React.ReactNode> = {
      pending: <Clock className="w-3 h-3" />, paid: <CheckCircle className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />, refunded: <RotateCcw className="w-3 h-3" />
    };
    const labels: Record<string, string> = { pending: 'En attente', paid: 'Payé', failed: 'Échoué', refunded: 'Remboursé' };
    return <Badge className={`${colors.bg} ${colors.text} border ${colors.border} gap-1`}>{icons[status]}{labels[status] || status}</Badge>;
  };

  const getPaymentMethodBadge = (method: string | undefined) => {
    if (!method) return <span className="text-gray-500">N/A</span>;
    const configs: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      mtn: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: <Smartphone className="w-3 h-3" /> },
      orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: <Smartphone className="w-3 h-3" /> },
      lygos: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: <CreditCard className="w-3 h-3" /> },
      stripe: { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: <CreditCard className="w-3 h-3" /> },
      paypal: { bg: 'bg-blue-600/20', text: 'text-blue-300', icon: <CreditCard className="w-3 h-3" /> },
    };
    const config = configs[method] || { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: <DollarSign className="w-3 h-3" /> };
    return <Badge className={`${config.bg} ${config.text} gap-1 capitalize`}>{config.icon}{method}</Badge>;
  };

  // ============================================================================
  // ÉCRAN DE CHARGEMENT
  // ============================================================================

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-green-500/20 border-t-green-500 animate-spin" />
            <DollarSign className="w-6 h-6 text-green-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-400 text-lg">Chargement des commandes...</p>
        </div>
      </AdminLayout>
    );
  }

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* HEADER PRINCIPAL */}
        <Card className="relative overflow-hidden bg-gradient-to-r from-green-600 via-emerald-600 to-teal-700 border-none shadow-2xl">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-20 w-40 h-40 bg-green-300/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          <CardContent className="relative py-8 px-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <Link to="/admin/dating">
                  <Button
                    variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/10 flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Retour</span>
                  </Button>
                </Link>
                <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg">
                  <Receipt className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-4xl font-bold text-white tracking-tight">Commandes Dating</h1>
                    <Badge className="bg-white/20 text-white border-white/30 text-sm px-3 py-1">{stats.total} commandes</Badge>
                  </div>
                  <p className="text-green-100 mt-2 text-base max-w-xl">
                    Gérez les commandes, suivez les paiements et analysez les revenus de votre plateforme de rencontres.
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-green-100/90">
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button variant="outline" onClick={handleExportCSV} className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm">
                  <Download className="w-4 h-4 mr-2" />Exporter CSV
                </Button>
                <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm">
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Actualiser
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* STATISTIQUES */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-green-500/20 via-green-500/10 to-emerald-500/5 border-green-500/30 hover:border-green-500/50 transition-all hover:shadow-lg hover:shadow-green-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-green-200/80">Revenus totaux</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatAmount(stats.revenue)}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {stats.revenueGrowth >= 0 ? (
                      <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs"><TrendingUp className="w-3 h-3 mr-1" />+{stats.revenueGrowth}%</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs"><TrendingDown className="w-3 h-3 mr-1" />{stats.revenueGrowth}%</Badge>
                    )}
                  </div>
                </div>
                <div className="p-2.5 bg-green-500/20 rounded-xl"><DollarSign className="w-6 h-6 text-green-300" /></div>
              </div>
              <div className="mt-4"><MiniBarChart data={weeklyRevenue} color="green" /><p className="text-xs text-green-200/60 mt-1">7 derniers jours</p></div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-cyan-500/5 border-blue-500/30 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-200/80">Payées</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.paid}</p>
                  <p className="text-xs text-blue-200/60 mt-3">Taux: {stats.conversionRate}%</p>
                </div>
                <CircularProgress value={stats.paid} max={stats.total} color="blue" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500/20 via-yellow-500/10 to-amber-500/5 border-yellow-500/30 hover:border-yellow-500/50 transition-all hover:shadow-lg hover:shadow-yellow-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-200/80">En attente</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.pending}</p>
                  <p className="text-xs text-yellow-200/60 mt-3">À traiter</p>
                </div>
                <div className="p-2.5 bg-yellow-500/20 rounded-xl"><Clock className="w-6 h-6 text-yellow-300" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 via-purple-500/10 to-violet-500/5 border-purple-500/30 hover:border-purple-500/50 transition-all hover:shadow-lg hover:shadow-purple-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-200/80">Panier moyen</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatAmount(stats.avgOrderValue)}</p>
                  <p className="text-xs text-purple-200/60 mt-3">Top: {stats.topPlan}</p>
                </div>
                <div className="p-2.5 bg-purple-500/20 rounded-xl"><Wallet className="w-6 h-6 text-purple-300" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-500/20 via-pink-500/10 to-rose-500/5 border-pink-500/30 hover:border-pink-500/50 transition-all hover:shadow-lg hover:shadow-pink-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-pink-200/80">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatAmount(stats.revenueToday)}</p>
                  <p className="text-xs text-pink-200/60 mt-3">Ce mois: {formatAmount(stats.revenueThisMonth)}</p>
                </div>
                <div className="p-2.5 bg-pink-500/20 rounded-xl"><Sparkles className="w-6 h-6 text-pink-300" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FILTRES AVANCÉS */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-center">
              {/* Recherche : large, comme dans DatingProfileList */}
              <div className="lg:col-span-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Rechercher par numéro, nom, email, plan..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400" 
                />
              </div>

              {/* Filtre Statut */}
              <div>
                <SimpleSelect
                  value={filterStatus}
                  onValueChange={(v) => setFilterStatus(v as FilterStatus)}
                  options={[
                    { value: 'all', label: 'Tous les statuts' },
                    { value: 'pending', label: '⏳ En attente' },
                    { value: 'paid', label: '✅ Payées' },
                    { value: 'failed', label: '❌ Échouées' },
                    { value: 'refunded', label: '💰 Remboursées' },
                  ]}
                />
              </div>

              {/* Filtre Paiement */}
              <div>
                <SimpleSelect
                  value={filterPaymentMethod}
                  onValueChange={(v) => setFilterPaymentMethod(v as FilterPaymentMethod)}
                  options={[
                    { value: 'all', label: 'Tous les paiements' },
                    { value: 'mtn', label: '📱 MTN Mobile' },
                    { value: 'orange', label: '📱 Orange Money' },
                    { value: 'lygos', label: '💳 Lygos' },
                    { value: 'stripe', label: '💳 Stripe' },
                    { value: 'paypal', label: '💳 PayPal' },
                  ]}
                />
              </div>

              {/* Filtre Période */}
              <div>
                <SimpleSelect
                  value={filterPeriod}
                  onValueChange={(v) => setFilterPeriod(v as FilterPeriod)}
                  options={[
                    { value: 'all', label: 'Toutes les périodes' },
                    { value: 'today', label: "📅 Aujourd'hui" },
                    { value: 'yesterday', label: '📅 Hier' },
                    { value: 'week', label: '📅 7 derniers jours' },
                    { value: 'month', label: '📅 Ce mois' },
                    { value: 'last_month', label: '📅 Mois dernier' },
                  ]}
                />
              </div>
            </div>
            
            {/* Résultats et réinitialisation */}
            {(filterStatus !== 'all' || filterPaymentMethod !== 'all' || filterPeriod !== 'all' || searchQuery) && (
              <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                <span>{filteredAndSortedOrders.length} résultat{filteredAndSortedOrders.length > 1 ? 's' : ''}</span>
                <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setFilterStatus('all'); setFilterPaymentMethod('all'); setFilterPeriod('all'); }} className="text-gray-400 hover:text-white">
                  <X className="w-3 h-3 mr-1" />Réinitialiser
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TABLE DES COMMANDES */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
          <CardContent className="p-0">
            {filteredAndSortedOrders.length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucune commande trouvée</p>
                <p className="text-gray-500 text-sm mt-1">Modifiez vos filtres ou attendez de nouvelles commandes</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10 hover:bg-transparent">
                      <TableHead className="text-gray-400 cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                        <div className="flex items-center gap-1">N° Commande {sortField === 'created_at' && <ArrowUpDown className="w-3 h-3" />}</div>
                      </TableHead>
                      <TableHead className="text-gray-400 cursor-pointer hover:text-white" onClick={() => handleSort('customer_name')}>
                        <div className="flex items-center gap-1">Client {sortField === 'customer_name' && <ArrowUpDown className="w-3 h-3" />}</div>
                      </TableHead>
                      <TableHead className="text-gray-400">Plan</TableHead>
                      <TableHead className="text-gray-400 cursor-pointer hover:text-white" onClick={() => handleSort('amount')}>
                        <div className="flex items-center gap-1">Montant {sortField === 'amount' && <ArrowUpDown className="w-3 h-3" />}</div>
                      </TableHead>
                      <TableHead className="text-gray-400">Paiement</TableHead>
                      <TableHead className="text-gray-400 cursor-pointer hover:text-white" onClick={() => handleSort('status')}>
                        <div className="flex items-center gap-1">Statut {sortField === 'status' && <ArrowUpDown className="w-3 h-3" />}</div>
                      </TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                      <TableHead className="text-right text-gray-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedOrders.map((order, index) => (
                      <TableRow key={order.id} className={`border-b border-white/5 hover:bg-white/5 ${index % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-white font-medium">{order.order_number}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-500 hover:text-white" onClick={() => handleCopyOrderNumber(order.order_number)}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {order.profile?.avatar_url ? (
                              <img src={order.profile.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                                <User className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-white flex items-center gap-1">
                                {order.customer_name || order.profile?.display_name}
                                {order.profile?.is_vip && <Crown className="w-3 h-3 text-yellow-400" />}
                                {order.profile?.is_verified && <ShieldCheck className="w-3 h-3 text-blue-400" />}
                              </div>
                              <div className="text-xs text-gray-500">{order.customer_email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-pink-500/30 text-pink-300">{order.plan?.name || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-semibold text-white">{formatAmount(order.amount)}</span>
                            {order.discount_amount > 0 && (
                              <div className="text-xs text-green-400">-{formatAmount(order.discount_amount)}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getPaymentMethodBadge(order.payment_method)}</TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="text-white">{format(new Date(order.created_at), 'dd/MM/yyyy', { locale: fr })}</div>
                            <div className="text-xs text-gray-500">{formatDistanceToNow(new Date(order.created_at), { addSuffix: true, locale: fr })}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10" onClick={() => { setSelectedOrder(order); setShowDetailModal(true); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {order.customer_email && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10" onClick={() => window.open(`mailto:${order.customer_email}?subject=Commande ${order.order_number}`, '_blank')}>
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                      Affichage {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredAndSortedOrders.length)} sur {filteredAndSortedOrders.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-white px-3">{currentPage} / {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* MODAL DÉTAILS */}
        {showDetailModal && selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowDetailModal(false)} />
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header Modal */}
              <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">Détails de la commande</h2>
                    <p className="text-green-100 text-sm mt-1 font-mono">{selectedOrder.order_number}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowDetailModal(false)} className="text-white/80 hover:text-white hover:bg-white/10">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Statut */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="font-medium text-white">Statut</span>
                  {getStatusBadge(selectedOrder.status)}
                </div>

                {/* Client */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center gap-2"><User className="w-4 h-4 text-pink-400" />Client</h4>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-2">
                    <div className="flex items-center gap-3">
                      {selectedOrder.profile?.avatar_url ? (
                        <img src={selectedOrder.profile.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-white flex items-center gap-2">
                          {selectedOrder.customer_name}
                          {selectedOrder.profile?.is_vip && <Crown className="w-4 h-4 text-yellow-400" />}
                        </p>
                        <p className="text-sm text-gray-400">{selectedOrder.customer_email}</p>
                      </div>
                    </div>
                    {selectedOrder.customer_phone && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm pt-2 border-t border-white/10">
                        <Phone className="w-4 h-4" />{selectedOrder.customer_phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* Plan */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center gap-2"><Crown className="w-4 h-4 text-yellow-400" />Plan</h4>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">{selectedOrder.plan?.name}</p>
                        <p className="text-sm text-gray-400">{selectedOrder.plan?.duration_days} jours</p>
                      </div>
                      {selectedOrder.plan?.is_vip && <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">VIP</Badge>}
                    </div>
                  </div>
                </div>

                {/* Montant */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" />Montant</h4>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-2">
                    <div className="flex justify-between text-gray-300">
                      <span>Sous-total</span>
                      <span>{formatAmount(selectedOrder.amount + selectedOrder.discount_amount)}</span>
                    </div>
                    {selectedOrder.discount_amount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Réduction ({selectedOrder.coupon_code})</span>
                        <span>-{formatAmount(selectedOrder.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t border-white/10 pt-2 text-white text-lg">
                      <span>Total</span>
                      <span>{formatAmount(selectedOrder.amount)}</span>
                    </div>
                  </div>
                </div>

                {/* Paiement */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-400" />Paiement</h4>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Méthode</span>
                      {getPaymentMethodBadge(selectedOrder.payment_method)}
                    </div>
                    {selectedOrder.payment_reference && (
                      <div className="flex justify-between text-gray-300">
                        <span className="text-gray-400">Référence</span>
                        <span className="font-mono text-sm">{selectedOrder.payment_reference}</span>
                      </div>
                    )}
                    {selectedOrder.paid_at && (
                      <div className="flex justify-between text-gray-300">
                        <span className="text-gray-400">Payé le</span>
                        <span>{format(new Date(selectedOrder.paid_at), 'dd/MM/yyyy HH:mm', { locale: fr })}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="text-sm text-gray-500 space-y-1 pt-2 border-t border-white/10">
                  <p>Créé le {format(new Date(selectedOrder.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}</p>
                  <p>Mis à jour le {format(new Date(selectedOrder.updated_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}</p>
                </div>
              </div>

              {/* Footer Modal */}
              <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur-sm border-t border-white/10 p-4 rounded-b-2xl flex flex-wrap gap-3 justify-end">
                {selectedOrder.status === 'pending' && (
                  <Button onClick={handleMarkAsPaid} disabled={processing} className="bg-green-500 hover:bg-green-600 text-white">
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Marquer comme payé
                  </Button>
                )}
                {selectedOrder.status === 'paid' && (
                  <Button onClick={handleRefund} disabled={processing} variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                    Rembourser
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailModal(false)} className="border-white/20 text-gray-300 hover:bg-white/5">
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
